<template>
	<AnalyticsPanel
		ref="panelRef"
		title="Audience"
		:subtitle="subtitle"
		:state="state"
		min-height="220px"
		:empty-title="activeTab?.emptyTitle || emptyTitle"
		:empty-text="activeTab?.emptyText || emptyText"
		:empty-retry="!activeTab?.optional"
		@retry="$emit('retry')"
	>
		<template #actions>
			<button
				v-if="rows.length && canExport"
				type="button"
				class="inline-flex size-7 items-center justify-center rounded-md text-surface-7 hover:bg-surface-3 hover:text-white"
				title="Export CSV"
				aria-label="Export CSV"
				data-testid="analytics-audience-export"
				@click="$emit('export')"
			>
				<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16"><icon-download /></icon-base>
			</button>
		</template>

		<template #toolbar>
			<!-- Narrow cards: one dropdown instead of a scrolling strip. -->
			<DropDownMenu
				v-if="narrow"
				type="outline"
				size="sm"
				position="left"
				wrapper-class="min-w-[10rem]"
				data-testid="analytics-audience-dropdown"
			>
				<template #toggle-button>
					<span class="truncate text-surface-7">Plays by:&nbsp;</span>
					<span class="truncate text-white">{{ activeTab?.label }}</span>
					<icon-base :width="12" :height="12" width-view-box="16" height-view-box="16" class="ml-1.5 shrink-0 text-surface-7"><icon-chevron-down /></icon-base>
				</template>
				<template #menu-items>
					<DropDownMenuItem
						v-for="t in tabs"
						:key="t.key"
						:class="tab === t.key ? '[&_button]:text-white [&_button]:bg-surface-3/60' : ''"
						@click="$emit('update:tab', t.key)"
					>
						{{ t.label }}
					</DropDownMenuItem>
				</template>
			</DropDownMenu>

			<!-- Otherwise a single-row segmented control that scrolls sideways (shared with the Trend tabs). -->
			<AnalyticsTabStrip
				v-else
				:tabs="tabs"
				:active="tab"
				aria-label="Audience breakdown"
				testid="analytics-audience-tabs"
				@update:active="$emit('update:tab', $event)"
			/>
		</template>

		<template #skeleton>
			<div class="flex flex-col gap-3 pt-1">
				<div v-for="(w, i) in [100, 70, 55, 40, 32, 24, 18, 12]" :key="i" class="flex items-center gap-3">
					<div class="h-3 animate-pulse rounded bg-surface-3/80" :class="labelWidthClass" />
					<div class="h-2.5 flex-1 rounded bg-surface-3/30"><div class="h-2.5 animate-pulse rounded bg-surface-3" :style="{ width: `${w}%` }" /></div>
					<div class="h-3 w-24 animate-pulse rounded bg-surface-3/60" />
				</div>
			</div>
		</template>

		<!-- Bars scale to the top row so the leader always fills the track. -->
		<ol class="flex flex-col gap-2.5" :data-testid="`analytics-audience-${tab}`">
			<li v-for="row in rows" :key="row.key ?? row.label" class="group flex items-center gap-3">
				<span class="shrink-0 truncate text-xs text-surface-text-1" :class="[labelWidthClass, { 'text-surface-7': row.isOther }]" :title="row.title || row.label">{{ row.label }}</span>
				<span class="relative h-2.5 min-w-0 flex-1 overflow-hidden rounded bg-surface-3/40">
					<span
						class="absolute inset-y-0 left-0 rounded transition-[width] duration-300"
						:class="row.isOther ? 'bg-surface-5' : 'bg-main-light'"
						:style="{ width: barWidth(row.value) }"
					/>
				</span>
				<span class="flex shrink-0 items-baseline justify-end gap-2 text-xs tabular-nums">
					<span class="min-w-[3rem] text-right text-surface-text-1">{{ formatNumber(row.value) }}</span>
					<span class="w-10 text-right text-surface-7">{{ formatShare(row.value, total) }}</span>
				</span>
			</li>
		</ol>
		<p v-if="footnote" class="mb-0 mt-3 text-[11px] leading-4 text-surface-6">{{ footnote }}</p>
	</AnalyticsPanel>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconDownload from '@/components/icon/IconDownload.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import AnalyticsPanel from './AnalyticsPanel.vue';
import AnalyticsTabStrip from './AnalyticsTabStrip.vue';
import { AUDIENCE_TABS, AUDIENCE_TAB_BY_KEY } from './analyticsMetrics';
import { formatNumber, formatShare } from './analyticsFormatters';

// Below this card width the tab strip collapses into a dropdown.
const NARROW_PX = 320;
const COMPACT_PX = 420;
const MIN_BAR_PX = 3;

const props = defineProps({
	tab: { type: String, required: true },
	// [{ key, label, value, share, isOther?, count? }]
	rows: { type: Array, default: () => [] },
	total: { type: Number, default: 0 },
	state: { type: String, default: 'ready' },
	canExport: { type: Boolean, default: true },
	// Copy for the empty state of the plays-based tabs (the optional
	// Captions / Audio tabs carry their own in AUDIENCE_TABS).
	emptyTitle: { type: String, default: 'No audience data for this period' },
	emptyText: { type: String, default: 'No player activity was recorded for the selected content and dates. Try a different date range or selection.' },
});

defineEmits(['update:tab', 'retry', 'export']);

const tabs = AUDIENCE_TABS;
const activeTab = computed(() => AUDIENCE_TAB_BY_KEY[props.tab]);

const subtitle = computed(() => {
	const t = activeTab.value;
	if (!t) return '';
	if (t.key === 'captions') return 'Plays by caption language. "Off" means no captions were shown.';
	if (t.key === 'audio') return 'Plays by selected audio track.';
	return `Plays by ${t.label.toLowerCase()}`;
});

const footnote = computed(() => {
	const other = props.rows.find((r) => r.isOther);
	if (!other) return '';
	return `Other groups ${other.count} smaller ${activeTab.value?.label?.toLowerCase() || 'value'} entries.`;
});

// ------------------------------------------------------------- bars
const maxValue = computed(() => props.rows.reduce((m, r) => Math.max(m, Number(r.value) || 0), 0));
// Fixed label column, sized by the card (not the viewport) so bars keep room.
const labelWidthClass = computed(() => {
	if (narrow.value) return 'w-20';
	return compact.value ? 'w-24' : 'w-36';
});

function barWidth(value) {
	const v = Number(value) || 0;
	if (!maxValue.value || v <= 0) return `${MIN_BAR_PX}px`;
	return `max(${MIN_BAR_PX}px, ${((v / maxValue.value) * 100).toFixed(2)}%)`;
}

// ------------------------------------------------- card width breakpoints
// The scrolling/drag/keyboard behaviour of the strip lives in AnalyticsTabStrip;
// this card only decides between the strip and the narrow dropdown.
const panelRef = ref(null);
const narrow = ref(false);
const compact = ref(false); // < COMPACT_PX: shorter label column
let resizeObserver = null;

onMounted(() => {
	const root = panelRef.value?.$el;
	if (root && typeof ResizeObserver !== 'undefined') {
		resizeObserver = new ResizeObserver(() => {
			// Border-box width: the threshold is about the card, not its padding box.
			const width = root.getBoundingClientRect().width;
			narrow.value = width > 0 && width < NARROW_PX;
			compact.value = width > 0 && width < COMPACT_PX;
		});
		resizeObserver.observe(root);
	}
});

onBeforeUnmount(() => {
	if (resizeObserver) resizeObserver.disconnect();
});
</script>
